import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { Checkpoint } from "./TaskCreationModal";

interface CheckpointsEditorProps {
  checkpoints: Checkpoint[];
  onChange: (checkpoints: Checkpoint[]) => void;
}

export const CheckpointsEditor = ({ checkpoints, onChange }: CheckpointsEditorProps) => {
  const [newCheckpointTitle, setNewCheckpointTitle] = useState("");

  const addCheckpoint = () => {
    if (!newCheckpointTitle.trim()) return;

    const newCheckpoint: Checkpoint = {
      id: `checkpoint-${Date.now()}`,
      title: newCheckpointTitle,
      completed: false,
      order: checkpoints.length
    };

    onChange([...checkpoints, newCheckpoint]);
    setNewCheckpointTitle("");
  };

  const removeCheckpoint = (id: string) => {
    onChange(checkpoints.filter(cp => cp.id !== id));
  };

  const moveCheckpoint = (index: number, direction: 'up' | 'down') => {
    const newCheckpoints = [...checkpoints];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newCheckpoints.length) return;
    
    [newCheckpoints[index], newCheckpoints[newIndex]] = [newCheckpoints[newIndex], newCheckpoints[index]];
    
    // Update order
    newCheckpoints.forEach((cp, idx) => {
      cp.order = idx;
    });
    
    onChange(newCheckpoints);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <p className="text-sm text-muted-foreground">
          ✅ Break down your task into smaller, actionable checkpoints
        </p>
      </div>

      {/* Add Checkpoint */}
      <div className="flex gap-2">
        <Input
          value={newCheckpointTitle}
          onChange={(e) => setNewCheckpointTitle(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addCheckpoint()}
          placeholder="Enter checkpoint title..."
          className="flex-1"
        />
        <Button onClick={addCheckpoint} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add
        </Button>
      </div>

      {/* Checkpoints List */}
      {checkpoints.length > 0 ? (
        <div className="space-y-2">
          {checkpoints.map((checkpoint, index) => (
            <Card key={checkpoint.id}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => moveCheckpoint(index, 'up')}
                    disabled={index === 0}
                  >
                    <GripVertical className="h-4 w-4" />
                  </Button>
                </div>
                
                <span className="text-muted-foreground">□</span>
                
                <div className="flex-1">
                  <p className="font-medium">{checkpoint.title}</p>
                </div>

                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCheckpoint(checkpoint.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p>No checkpoints added yet.</p>
          <p className="text-sm">Add checkpoints to break down your task into manageable steps.</p>
        </div>
      )}
    </div>
  );
};